import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Box, Typography, Paper, TextField, Button, Spinner, Alert, Chip, EmptyState } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, type AuditLogEntry, type AuditLogFilters } from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';

/** Seed filters from the URL so deep-links (e.g. from the Users page —
 *  ?entityType=user&entityId=GLPU…) open the log pre-filtered. */
function filtersFromParams(params: URLSearchParams): AuditLogFilters {
  const out: AuditLogFilters = {};
  for (const key of ['actorSub', 'entityType', 'entityId', 'capability', 'action'] as const) {
    const v = params.get(key);
    if (v) out[key] = v;
  }
  return out;
}

/**
 * Audit-log viewer (audit:read) — a read-only window onto AdminAuditLog so every
 * back-office action is reviewable. Newest-first, filterable by actor / entity /
 * capability / action, with keyset "Load more" pagination. Read-only: viewing
 * the log records nothing.
 */
export default function AuditLog() {
  const caps = useCapabilities();
  const canRead = caps.has('audit:read');

  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Draft filter inputs vs. the applied filter set (applied on "Apply").
  // Seed both from URL params so a deep-link opens pre-filtered.
  const [searchParams] = useSearchParams();
  const [draft, setDraft] = useState<AuditLogFilters>(() => filtersFromParams(searchParams));
  const [applied, setApplied] = useState<AuditLogFilters>(() => filtersFromParams(searchParams));

  const load = useCallback(async (filters: AuditLogFilters) => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listAuditLog(filters);
      setItems(res.items);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the audit log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canRead) void load(applied);
  }, [canRead, applied, load]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await adminApiClient.listAuditLog({ ...applied, before: nextCursor });
      setItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const setField = (key: keyof AuditLogFilters) => (e: { target: { value: string } }) =>
    setDraft((d) => ({ ...d, [key]: e.target.value }));

  if (caps.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }
  if (!canRead) {
    return <Alert severity="error">You don’t have permission to view the audit log.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Audit log
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Every admin action, newest first.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextField label="Actor (sub)" value={draft.actorSub ?? ''} onChange={setField('actorSub')} size="small" sx={{ width: 200 }} />
          <TextField label="Entity type" value={draft.entityType ?? ''} onChange={setField('entityType')} size="small" sx={{ width: 150 }} />
          <TextField label="Capability" value={draft.capability ?? ''} onChange={setField('capability')} size="small" sx={{ width: 160 }} />
          <TextField label="Action" value={draft.action ?? ''} onChange={setField('action')} size="small" sx={{ width: 150 }} />
          <Button
            variant="contained"
            ariaLabel="Apply filters"
            buttonText="Apply"
            onClick={() => setApplied({ ...draft })}
          />
          <Button
            variant="outlined"
            sx={NEUTRAL_BTN}
            ariaLabel="Clear filters"
            buttonText="Clear"
            onClick={() => {
              setDraft({});
              setApplied({});
            }}
          />
        </Box>
      </Paper>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner color="brand" />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState icon="history" title="No entries" body="No audit entries match these filters." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((entry) => (
            <AuditRow key={entry.id} entry={entry} />
          ))}
          {nextCursor && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button
                variant="outlined"
                sx={NEUTRAL_BTN}
                ariaLabel="Load more audit entries"
                buttonText={loadingMore ? 'Loading…' : 'Load more'}
                disabled={loadingMore}
                onClick={() => void loadMore()}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const [open, setOpen] = useState(false);
  const hasDiff = entry.before != null || entry.after != null;

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip label={entry.action} color="primary" size="small" />
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {entry.entityType}
          {entry.entityId ? ` · ${entry.entityId}` : ''}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {new Date(entry.createdAt).toLocaleString()}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={entry.actorRole} size="small" />
        <Chip label={entry.capability} size="small" />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          actor: {entry.actorSub}
        </Typography>
        {hasDiff && (
          <Button
            variant="text"
            ariaLabel={open ? 'Hide changes' : 'Show changes'}
            buttonText={open ? 'Hide changes' : 'Show changes'}
            onClick={() => setOpen((v) => !v)}
          />
        )}
      </Box>
      {open && hasDiff && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1.5 }}>
          <JsonBlock label="Before" value={entry.before} />
          <JsonBlock label="After" value={entry.after} />
        </Box>
      )}
    </Paper>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
        {label}
      </Typography>
      <Box
        sx={{
          mt: 0.5,
          p: 1.5,
          bgcolor: '#f5f5f5',
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: 12,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {value == null ? '—' : JSON.stringify(value, null, 2)}
      </Box>
    </Box>
  );
}
